<script setup>
import { ref, watch } from "vue";
import { Pen } from "lucide-vue-next";
import LoaderComponent from "@/components/ui/Loader.vue";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Operating Hours",
  },
  description: {
    type: String,
    default: "Configure when you are available for sessions.",
  },
  editable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:modelValue", "save"]);

const isEditing = ref(false);
const draftOperatingHours = ref({});

const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const toggleEdit = () => {
  isEditing.value = true;
  draftOperatingHours.value = parseOperatingHoursForEdit(props.modelValue);
};

const cancelEdit = () => {
  isEditing.value = false;
};

const parseOperatingHoursForEdit = (hoursObj) => {
  const draft = {};
  days.forEach((day) => {
    const val = hoursObj?.[day];
    if (!val || val.toLowerCase() === "closed") {
      draft[day] = { closed: true, open: "09:00", close: "17:00" };
    } else {
      const parts = val.split("-");
      draft[day] = {
        closed: false,
        open: parts[0] || "09:00",
        close: parts[1] || "17:00",
      };
    }
  });
  return draft;
};

const save = () => {
  const formatted = {};
  days.forEach((day) => {
    const data = draftOperatingHours.value[day];
    if (data.closed) {
      formatted[day] = "closed";
    } else {
      formatted[day] = `${data.open}-${data.close}`;
    }
  });
  emit("save", formatted);
};

// Handle exit edit mode when parent confirms save
watch(
  () => props.isSaving,
  (newVal, oldVal) => {
    if (oldVal && !newVal && isEditing.value) {
      isEditing.value = false;
    }
  },
);
</script>

<template>
  <div class="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
    <!-- Title & Actions -->
    <div
      class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border pb-6"
    >
      <div>
        <h3 class="text-[16px] font-bold text-foreground">
          {{ title }}
        </h3>
        <p v-if="description" class="text-sm text-muted-foreground mt-1">
          {{ description }}
        </p>
      </div>

      <div class="flex items-center gap-3" v-if="editable">
        <button
          v-if="!isEditing"
          @click="toggleEdit"
          class="px-5 py-2.5 rounded-full border border-border bg-card text-foreground text-sm font-semibold hover:bg-muted transition-colors flex items-center justify-center gap-2 shadow-sm"
        >
          <Pen class="w-4 h-4" />
          Edit Hours
        </button>
        <div v-else class="flex items-center gap-2">
          <button
            @click="cancelEdit"
            :disabled="isSaving"
            class="px-5 py-2.5 rounded-full border border-border bg-card text-foreground text-sm font-semibold hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            @click="save"
            :disabled="isSaving"
            class="px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 shadow-sm"
          >
            <LoaderComponent v-if="isSaving" class="w-4 h-4 border-white" />
            <span v-else>Save Changes</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Availability Content Area -->
    <div class="bg-muted/10 rounded-2xl border border-border overflow-hidden">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-20">
        <LoaderComponent class="w-8 h-8 text-primary" />
      </div>

      <!-- Read-Only View -->
      <div v-else-if="!isEditing" class="divide-y divide-border">
        <div
          v-for="day in days"
          :key="day"
          class="flex items-center justify-between p-5 hover:bg-muted/30 transition-colors"
        >
          <div class="flex items-center gap-4 w-1/3">
            <span
              class="text-[14px] font-bold text-foreground capitalize w-24"
              >{{ day }}</span
            >
          </div>

          <div class="flex-1 flex items-center justify-end md:justify-start">
            <template
              v-if="
                !modelValue[day] || modelValue[day].toLowerCase() === 'closed'
              "
            >
              <span
                class="text-red-500 font-semibold text-[14px] bg-red-500/10 px-3 py-1 rounded-md"
                >Closed</span
              >
            </template>
            <template v-else>
              <div
                class="flex items-center gap-3 bg-card border border-border px-3 py-1.5 rounded-lg shadow-sm"
              >
                <span class="text-[14px] font-medium text-foreground">{{
                  modelValue[day].split("-")[0]
                }}</span>
                <span class="text-muted-foreground/50">-</span>
                <span class="text-[14px] font-medium text-foreground">{{
                  modelValue[day].split("-")[1]
                }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Edit Form View -->
      <div v-else class="divide-y divide-border">
        <div
          v-for="day in days"
          :key="day"
          class="flex flex-col md:flex-row md:items-center justify-between p-5 gap-4"
        >
          <div class="flex items-center gap-4 w-full md:w-1/3">
            <span
              class="text-[14px] font-bold text-foreground capitalize w-24"
              >{{ day }}</span
            >
            <label
              class="flex items-center gap-2 cursor-pointer flex-1 md:flex-none"
            >
              <input
                type="checkbox"
                v-model="draftOperatingHours[day].closed"
                class="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
              />
              <span class="text-sm text-muted-foreground font-medium"
                >Closed</span
              >
            </label>
          </div>

          <div
            class="flex-1 flex gap-2 w-full"
            :class="{
              'opacity-50 pointer-events-none': draftOperatingHours[day].closed,
            }"
          >
            <div class="relative flex-1 group">
              <input
                type="time"
                v-model="draftOperatingHours[day].open"
                :disabled="draftOperatingHours[day].closed"
                class="w-full px-4 py-2 bg-card border border-border rounded-xl text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all shadow-sm text-center"
              />
            </div>
            <span
              class="text-muted-foreground flex items-center px-1 font-medium"
              >to</span
            >
            <div class="relative flex-1 group">
              <input
                type="time"
                v-model="draftOperatingHours[day].close"
                :disabled="draftOperatingHours[day].closed"
                class="w-full px-4 py-2 bg-card border border-border rounded-xl text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all shadow-sm text-center"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
